/**
 * NotificationStatsRemapper
 *
 * Bridges Firestore wire JSON ↔ `NotificationStatsDto` ↔ `NotificationStatsEntity`,
 * following the `fromJson(): Dto` + `fromDto(): Entity` convention codified by
 * `chatMessageRemapper.ts` and `data-layer.md`.
 * Flutter source: lib/data/models/notification_stats.dart (no @JsonKey — Dart
 * property == Firestore field). All fields are nullable in Flutter, so each maps
 * straight through and stays `undefined` when absent. The DTO and entity are
 * field-identical, so `fromDto` is a 1:1 copy.
 */
import type { JsonObject } from '@/data/types/json';
import type { NotificationStatsDto } from '@/data/dtos/info/notificationStatsDto';
import type { NotificationStatsEntity } from '@/domain/entities/info/notificationStatsEntity';

export class NotificationStatsRemapper {
  /** Parse raw Firestore JSON into a `NotificationStatsDto`. */
  static fromJson(data: JsonObject): NotificationStatsDto {
    return {
      contentLikes: data['contentLikes'] as number | undefined,
      contentComments: data['contentComments'] as number | undefined,
      commentLikes: data['commentLikes'] as number | undefined,
      commentReplies: data['commentReplies'] as number | undefined,
      coins: data['coins'] as number | undefined,
      subscribers: data['subscribers'] as number | undefined,
      donations: data['donations'] as number | undefined,
    };
  }

  static fromJsonList(dataList: JsonObject[]): NotificationStatsDto[] {
    return dataList.map(NotificationStatsRemapper.fromJson);
  }

  /**
   * Build a `NotificationStatsEntity` from a `NotificationStatsDto`. The DTO and
   * entity are field-identical (all nullable primitives), so this is a 1:1 copy.
   */
  static fromDto(dto: NotificationStatsDto): NotificationStatsEntity {
    return {
      contentLikes: dto.contentLikes,
      contentComments: dto.contentComments,
      commentLikes: dto.commentLikes,
      commentReplies: dto.commentReplies,
      coins: dto.coins,
      subscribers: dto.subscribers,
      donations: dto.donations,
    };
  }
}
